import { makeSnapshot, TODAY } from 'components/Streaks/model/fixtures';
import {
  currentMilestone,
  DEFAULT_MILESTONES,
  sortedMilestones
} from 'components/Streaks/model/milestones';
import { StreakSnapshot } from 'components/Streaks/model/types';

/**
 * Synthetic snapshots that make the at-table moment (EN-11163) play on demand,
 * for the Storybook felt and the /demo-table "Streak moment" controls, so the
 * animation can be iterated on without earning a streak. `run` bumps the
 * version and hand id, so each replay is a new moment.
 */
export type MomentDemoKind = 'daily' | 'milestone' | 'first' | 'preflop' | 'watching';

export const MOMENT_DEMO_KINDS: { kind: MomentDemoKind; label: string }[] = [
  { kind: 'daily', label: 'Daily' },
  { kind: 'milestone', label: 'Milestone' },
  { kind: 'first', label: 'First day' },
  { kind: 'preflop', label: 'Won preflop' },
  { kind: 'watching', label: 'Watching' }
];

/** The day each kind counts when no day is picked. */
export const MOMENT_DEMO_DEFAULT_DAYS = 9;

/**
 * The day a kind actually plays for a picked streak day: a first day is day 1,
 * a milestone is the picked day when it is a milestone day, else the next one
 * up (the last one past the end of the ladder), so one input reaches every tier.
 * Any other play on a milestone day plays that milestone (momentDemoSnapshot).
 */
export function momentDemoDay(kind: MomentDemoKind, days: number): number {
  if (kind === 'first') return 1;
  const picked = Math.max(1, Math.floor(days));
  if (kind !== 'milestone') return picked;
  const ladder = sortedMilestones(DEFAULT_MILESTONES);
  return (ladder.find((m) => m.days >= picked) ?? ladder[ladder.length - 1]).days;
}

/**
 * Far above any real snapshot's version, so a signed-in player's own sync
 * (useStreakSync) cannot replace a demo play halfway through it.
 */
const DEMO_VERSION_BASE = 1_000_000_000;

/** Which burst a play of `kind` at `days` gets (its flames and its length). */
export function momentDemoBurst(
  kind: MomentDemoKind,
  days: number
): 'daily' | 'first' | 'milestone' {
  const day = momentDemoDay(kind, days);
  if (kind === 'first' || day === 1) return 'first';
  return kind === 'milestone' || DEFAULT_MILESTONES.some((m) => m.days === day)
    ? 'milestone'
    : 'daily';
}

export function momentDemoSnapshot(
  kind: MomentDemoKind,
  run: number,
  gameID = 4417,
  days = MOMENT_DEMO_DEFAULT_DAYS
): StreakSnapshot {
  const handId = `texas:${gameID}:${run}`;
  const where = { handId, gameType: 'texas' as const, gameId: gameID };
  const day = momentDemoDay(kind, days);
  const holding = currentMilestone(DEFAULT_MILESTONES, day)?.key ?? null;
  const streak = {
    version: DEMO_VERSION_BASE + run,
    status: 'live' as const,
    currentStreak: day,
    // Day 1 with a best of 1 is a streak's first day (planMoment).
    bestStreak: day === 1 ? 1 : Math.max(day, 12),
    lastQualifiedDay: TODAY,
    currentMilestoneKey: holding,
    highestMilestoneKey: holding
  };
  // A day that is a milestone day is a milestone, as the server sends it,
  // whichever button played it (a first day never is).
  const reached = DEFAULT_MILESTONES.some((m) => m.days === day);
  if ((kind === 'milestone' || (reached && kind !== 'first')) && holding) {
    return makeSnapshot({
      ...streak,
      moment: { kind: 'milestone', ...where, wonPreflop: kind === 'preflop' },
      notice: { milestoneReached: { key: holding, day: TODAY } }
    });
  }
  return makeSnapshot({
    ...streak,
    moment: { kind: 'counted', ...where, wonPreflop: kind === 'preflop' }
  });
}
